import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import RegistrationForm from './components/RegistrationForm';
import FeatureHighlights from './components/FeatureHighlights';
import ProgressIndicator from './components/ProgressIndicator';
import TestimonialSection from './components/TestimonialSection';

const Register = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Main Content */}
      <main className="pt-[60px] pb-20 md:pb-8">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Hero Section */}
            <div className="text-center mb-12">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                  <Icon name="Zap" size={24} color="white" />
                </div>
                <h1 className="text-4xl font-bold text-foreground">CryptoWizard Pro</h1>
              </div>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Advanced cryptocurrency trading platform with professional-grade analytics and the revolutionary PTS-WIZARD-V3 algorithm
              </p>
            </div>

            {/* Main Registration Layout */}
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left Column - Progress & Features */}
              <div className="lg:col-span-1 space-y-8">
                {/* Progress Indicator */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <ProgressIndicator currentStep={1} />
                </div>

                {/* Feature Highlights - Desktop Only */}
                <div className="hidden lg:block bg-card border border-border rounded-xl p-6">
                  <FeatureHighlights />
                </div>
              </div>

              {/* Center Column - Registration Form */}
              <div className="lg:col-span-1">
                <div className="bg-card border border-border rounded-xl p-8 shadow-lg">
                  <RegistrationForm />
                </div>
              </div>

              {/* Right Column - Testimonials & Trust Signals */}
              <div className="lg:col-span-1 space-y-8">
                {/* Testimonials */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <TestimonialSection />
                </div>

                {/* Security Features */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="text-center mb-6">
                    <Icon name="Shield" size={32} color="var(--color-success)" className="mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Enterprise Security
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Your data and assets are protected by military-grade encryption
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Icon name="Lock" size={16} color="var(--color-success)" />
                      <span className="text-sm text-foreground">256-bit SSL encryption</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Shield" size={16} color="var(--color-success)" />
                      <span className="text-sm text-foreground">Two-factor authentication</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Database" size={16} color="var(--color-success)" />
                      <span className="text-sm text-foreground">Cold storage protection</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Eye" size={16} color="var(--color-success)" />
                      <span className="text-sm text-foreground">24/7 monitoring</span>
                    </div>
                  </div>
                </div>

                {/* Regulatory Compliance */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="text-center mb-4">
                    <Icon name="Award" size={24} color="var(--color-accent)" className="mx-auto mb-2" />
                    <h4 className="text-lg font-medium text-foreground">Regulatory Compliance</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mx-auto mb-1" />
                      <span className="text-xs font-medium text-foreground">SEC Registered</span>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mx-auto mb-1" />
                      <span className="text-xs font-medium text-foreground">FINRA Member</span>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mx-auto mb-1" />
                      <span className="text-xs font-medium text-foreground">GDPR Compliant</span>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mx-auto mb-1" />
                      <span className="text-xs font-medium text-foreground">ISO 27001</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Feature Highlights */}
            <div className="lg:hidden mt-8">
              <div className="bg-card border border-border rounded-xl p-6">
                <FeatureHighlights />
              </div>
            </div>

            {/* Bottom CTA Section */}
            <div className="mt-12 text-center">
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-xl p-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  Ready to Start Trading Like a Pro?
                </h2>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Join thousands of successful traders who use CryptoWizard Pro to maximize their cryptocurrency investments with advanced analytics and real-time market insights.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Clock" size={16} />
                    <span>Setup takes less than 5 minutes</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="CreditCard" size={16} />
                    <span>No credit card required</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Smartphone" size={16} />
                    <span>Available on all devices</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="bg-card border-t border-border py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-4">
                <Link to="/trading-dashboard" className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-primary rounded-md flex items-center justify-center">
                    <Icon name="Zap" size={14} color="white" />
                  </div>
                  <span className="font-semibold text-foreground">CryptoWizard Pro</span>
                </Link>
                <span className="text-sm text-muted-foreground">
                  © {new Date()?.getFullYear()} All rights reserved.
                </span>
              </div>
              <div className="flex items-center space-x-6">
                <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Terms
                </Link>
                <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Privacy
                </Link>
                <Link to="/support" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Register;